<template>
    <c-modal :value="isOpen" title="Map Styles" size="xl" @closed="handleClose">
        <map-style-properties-create-header />
        <div class="presets-content">
            <div v-if="!filteredPresets?.length" class="no-results">
                <font-awesome-icon :icon="['fa-solid', 'fa-search']" class="no-results-icon" />
                <h4>No styles found</h4>
                <p>Try adjusting your search terms</p>
            </div>
            <template v-else>
                <div class="presets-grid">
                    <map-style-properties-create-thumbnail
                        v-for="(preset, index) in filteredPresets"
                        :key="index"
                        :preset="preset"
                        @select="handlePresetSelect"
                    />
                </div>
            </template>
        </div>
        <template #modal-footer>
            <div class="d-flex justify-content-end">
                <c-button text="Cancel" variant="secondary" @click="handleClose" />
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue';
import store from '@/store';
import CModal from '@/components/CModal.vue';
import CButton from '@/components/shared/CButton';
import { MapStylePresetType } from '@/components/DesignStudio/MapStyleProperties/types';
import MapStylePropertiesCreateThumbnail from '@/components/DesignStudio/MapStyleProperties/components/create/thumbnail/MapStylePropertiesCreateThumbnail.vue';
import MapStylePropertiesCreateHeader from '@/components/DesignStudio/MapStyleProperties/components/create/header/MapStylePropertiesCreateHeader.vue';
import { MAP_STYLE_PRESETS } from '@/components/DesignStudio/MapStyleProperties/presets';

interface Emits {
    (event: 'create', value: string): void;
}

const emit = defineEmits<Emits>();

const isOpen = computed(() => {
    return store.state.mapStyleProperties?.modalAddIsOpen || false;
});

const filteredPresets = computed(() => {
    const searchQuery = store.state.mapStyleProperties?.searchQuery.toLowerCase() || '';
    return store.state.mapStyleProperties?.searchQuery
        ? MAP_STYLE_PRESETS.filter((preset) => preset.label.toLowerCase().includes(searchQuery))
        : MAP_STYLE_PRESETS;
});

const handleClose = () => {
    store?.dispatch('mapStyleProperties/closeAddModal');
};

const resetForm = () => {
    store.dispatch('mapStyleProperties/resetModalState');
};

const handlePresetSelect = (preset: MapStylePresetType) => {
    emit('create', preset.value);
    handleClose();
};

watch(
    () => isOpen.value,
    () => {
        if (!isOpen.value) {
            resetForm();
        }
    }
);
</script>

<style scoped lang="scss">
.no-results {
    text-align: center;
    padding: 48px 24px;
    color: #6c757d;

    .no-results-icon {
        font-size: 48px;
        margin-bottom: 16px;
        color: #dee2e6;
    }

    h4 {
        margin: 0 0 8px;
        font-size: 18px;
    }

    p {
        margin: 0;
        font-size: 14px;
    }
}

.presets-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 16px;
    max-height: 400px;
    overflow-y: auto;
}

.grouped-presets {
    overflow-y: auto;
    .presets-grid {
        max-height: none;
        overflow-y: visible;
        margin-bottom: 0;
    }
}

.presets-grid::-webkit-scrollbar {
    width: 6px;
}

.presets-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.presets-grid::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
        background: #a8a8a8;
    }
}

@media (max-width: 768px) {
    .presets-grid {
        grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
        gap: 8px;
    }
}
</style>
